import { DisabledPrompt, Link, useWorkflowStep } from "@bcm-dev/bcms-vite";
import CustomTableAccordion, { CustomTableContent, CustomTableToggle, useCustomTableAccordion } from "components/custom-ui/CustomTableAccordion";
import { CenterContentContainer, ContentHeaderView, FormContainer, FormRowView, PageContainer, PageContentView, TableColumnContainer, TableColumnView, TableItemTextView, TableItemView, TableView } from "components/shared";
import { SecondaryButton } from "components/ui";
import { getDefault, getWorkflowSteps } from "components/workflows/TransferAccountsWorkflow";
import React from "react";
import { SVGAsset } from "res";
import MobileExternalAccountsTable from "./components/MobileExternalAccountsTable";
import "./styles.css";
import "./mobile.css";


/**
 * @function TransferAccountsPage
 * @return {React.Component}
 * @exported true
 * @description The main component for TransferAccountsPage
 */

export default function TransferAccountsPage(props) {
  const workflowStep = useWorkflowStep(getDefault, getWorkflowSteps);
  const accounts = workflowStep.read("externalAccounts");
  
  return (
    <PageContainer className="transfer-accounts-page">
      <PageContentView>
        <CenterContentContainer>
          <ContentHeaderView text="Transfer Accounts" dataPoint="Transfer_Accounts_Header"/>
          <DisabledPrompt>
            <SecondaryButton text="Add Account"  dataPoint="Add_Accounts_Button"/>
          </DisabledPrompt>
          <h1 className="section-header">
            <span data-point="External_Accounts_Header" className="external-accounts-header">External Accounts</span>
          </h1>

          <div className="show-on-desktop">
            <ExternalAccountTableAccordion accounts={ accounts } />
          </div>
          <div className="show-on-mobile">
            <MobileExternalAccountsTable accounts={ accounts } />
          </div>
        </CenterContentContainer>
      </PageContentView>
    </PageContainer>
  );
}

/**
 * @function ExternalAccountTableAccordion
 * @return {React.Component}
 * @exported false
 * @description The second workflow step for the ExternalAccountTableAccordion
 */

function ExternalAccountTableAccordion(props){
    const accounts = props.accounts ?? [];
    const rows = accounts.reduce((acc, account) => {
      acc.push(
        <CustomTableAccordion activeClass="open-custom-account-accordion" state={ false } key={ account.id }>
            <ExternalAccountTableToggle account={ account } />
            <ExternalAccountTableContent account={ account } />
        </CustomTableAccordion>
      );
      return acc;
    }, []);

    return(
      <TableView className="transfer-accounts-table">
        <TableColumnContainer>
          <TableColumnView text="" />
          <TableColumnView className="text-left" text={<>Nickname <DisabledPrompt><SVGAsset id="ArrowUpIcon" className="clickable" /></DisabledPrompt></>} />
          <TableColumnView className="text-left" text={<>Financial Institution <DisabledPrompt><SVGAsset id="SortIcon" className="clickable" /></DisabledPrompt></>} />
          <TableColumnView className="text-left" text={<>Account Type <DisabledPrompt><SVGAsset id="SortIcon" className="clickable" /></DisabledPrompt></>} />
          <TableColumnView className="text-left" text="Account" />
          <TableColumnView className="text-left" text={<>Status <DisabledPrompt><SVGAsset id="SortIcon" className="clickable" /></DisabledPrompt></>} />
        </TableColumnContainer>
        { rows }
      </TableView>
    );
}

/**
 * @function ExternalAccountTableToggle
 * @param {String} accountNickname - The name of the account
 * @param {String} fiName - The name of the financial institution
 * @param {String} accountType - the type of account
 * @param {String} accountLastFour - the account number last four digits 
 * @param {String} accountStatus - the status of the account
 * @return {React.Component}
 * @exported false
 * @description The ExternalAccountTableToggle is the toggle of the ExternalAccountTableAccordion
 * @example
 * <ExternalAccountTableToggle accountNickname="External Checking Account" fiName="FI Credit Union" accountType="Checking" accountLastFour="x1234" accountStatus="Active" />
 */


function ExternalAccountTableToggle(props) {
  const { state } = useCustomTableAccordion();
  const account = props.account ?? {};

  return(
    <CustomTableToggle>
      <tr>
        <TableItemView className="expand-row-chevron text-center">
          <SVGAsset  id={ state ? "DownChevronIcon" : "RightChevronIcon" } className="icon"  data-point={"Transfer_cheveron_"+account.id}/>
        </TableItemView>
        <TableItemTextView text={ account.nickname }  dataPoint={"Transfer_nickname_"+account.id}/>
        <TableItemTextView text={ account.institutionName }  dataPoint={"Transfer_institutionName_"+account.id}/>
        <TableItemTextView text={ account.type }  dataPoint={"Transfer_type_"+account.id}/>
        <TableItemTextView text={ account.number }  dataPoint={"Transfer_number_"+account.id}/>
        <TableItemTextView text={ account.externalStatus }  dataPoint={"Transfer_externalStatus_"+account.id}/>
      </tr>
    </CustomTableToggle>
  );
}

/**
 * @function ExternalAccountTableContent
 * @param {String} routingNumber - The routing number of the account
 * @param {String} dateAdded - The date the account was added
 * @return {React.Component}
 * @exported false
 * @description The ExternalAccountTableContent is the content shown of the ExternalAccountTableAccordion
 * @example
 * <ExternalAccountTableContent routingNumber="123456789" dateAdded="01/01/2023" />
 */

function ExternalAccountTableContent(props) {
  const account = props.account ?? {};

  return(
    <CustomTableContent> 
      <tr>
      <td className="transfer-accounts-table-item" colSpan="6">
        <div className="flex-container">
          <p className="flex-width">ACCOUNT DETAILS</p>         
          <CustomTableToggle><SVGAsset id="CloseX" className="set-width clickable" /> </CustomTableToggle>          
        </div>
        <FormContainer>
          <FormRowView text="Routing Number">
            <p>{ account.routingNumber }</p>
          </FormRowView>
          <FormRowView text="Date Added">
            <p>{ account.dateOpened.format('m/d/Y') }</p>
          </FormRowView>
          <FormRowView className="link-container">
            <DisabledPrompt>
              <Link>Edit Account</Link>
            </DisabledPrompt>
          </FormRowView>
          <FormRowView className="link-container">
            <DisabledPrompt>
              <Link>Remove Account</Link>
            </DisabledPrompt>
          </FormRowView>
        </FormContainer>
      </td>      
      </tr>   
      </CustomTableContent>
  );
}
